<template>
  <MarketingLayout>
    <template v-if="guide">
      <router-link :to="backLink" class="back-link">← {{ backLabel }}</router-link>
      <article class="guide-article">
        <span class="guide-tag">{{ guide.tag }}</span>
        <h1>{{ guide.title }}</h1>
        <p class="guide-meta">{{ guide.readTime }}</p>
        <div class="guide-body">
          <template v-for="(block, i) in guide.body" :key="i">
            <h3 v-if="block.type === 'h3'">{{ block.content }}</h3>
            <ul v-else-if="block.type === 'ul'">
              <li v-for="(item, j) in block.content" :key="j">{{ item }}</li>
            </ul>
            <p v-else>{{ block.content }}</p>
          </template>
        </div>
      </article>

      <section class="cta-strip">
        <div>
          <h3>Ready to put this into practice?</h3>
          <p>See how WinCard automates the retention playbook end to end.</p>
        </div>
        <router-link class="button" to="/contact">Get in touch <span>→</span></router-link>
      </section>
    </template>

    <template v-else>
      <div class="not-found">
        <p class="eyebrow">GUIDES</p>
        <h1>Guide not found.</h1>
        <p class="lead">This one may have moved. Head back to browse everything we’ve got.</p>
        <router-link class="button" :to="backLink">{{ backLabel }} <span>→</span></router-link>
      </div>
    </template>
  </MarketingLayout>
</template>

<script>
import MarketingLayout from '@/components/MarketingLayout.vue'
import { guides, retentionGuides } from '@/data/guides'
import { useSeo, SITE_NAME } from '@/composables/useSeo'

export default {
  name: 'GuideDetailPage',
  components: { MarketingLayout },
  props: {
    collection: { type: String, required: true },
    slug: { type: String, required: true },
  },
  setup(props) {
    useSeo(() => {
      const list = props.collection === 'retention-guides' ? retentionGuides : guides
      const guide = list.find(g => g.slug === props.slug)
      const path = `/${props.collection}/${props.slug}`

      if (!guide) {
        return { title: 'Guide not found', description: 'This guide may have moved.', path }
      }

      return {
        title: guide.title,
        description: guide.description,
        path,
        type: 'article',
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: guide.title,
          description: guide.description,
          author: { '@type': 'Organization', name: SITE_NAME },
          publisher: { '@type': 'Organization', name: SITE_NAME },
        },
      }
    });
  },
  computed: {
    list() { return this.collection === 'retention-guides' ? retentionGuides : guides },
    guide() { return this.list.find(g => g.slug === this.slug) || null },
    backLink() { return this.collection === 'retention-guides' ? '/retention-guides' : '/guides' },
    backLabel() { return this.collection === 'retention-guides' ? 'Retention guides' : 'Guides' },
  },
}
</script>

<style scoped>
.back-link { display: inline-block; font-size: 13px; font-weight: 600; color: var(--muted); margin-bottom: 30px; }
.back-link:hover { color: var(--green); }

.guide-article { max-width: 680px; margin: 0 auto; }
.guide-tag { font: 500 10px var(--mono); letter-spacing: .8px; color: var(--green); }
.guide-article h1 { font-size: 42px; letter-spacing: -2px; line-height: 1.05; margin: 12px 0 10px; font-weight: 600; }
.guide-article > .guide-meta { font-size: 12px; color: #9aa3a0; margin: 0 0 40px; }
.guide-body h3 { font-size: 19px; letter-spacing: -.5px; margin: 34px 0 12px; }
.guide-body p { font-size: 15px; line-height: 1.7; color: #3d443f; margin: 0 0 18px; }
.guide-body ul { margin: 0 0 18px; padding-left: 20px; }
.guide-body li { font-size: 15px; line-height: 1.7; color: #3d443f; margin: 6px 0; }

.cta-strip { max-width: 680px; margin: 70px auto 0; background: var(--cream); border: 1px solid #ebefeb; border-radius: 12px; padding: 34px 40px; display: flex; align-items: center; justify-content: space-between; gap: 20px; }
.cta-strip h3 { margin: 0 0 4px; font-size: 19px; }
.cta-strip p { margin: 0; color: var(--muted); font-size: 13px; }
.cta-strip .button { white-space: nowrap; }

.not-found { max-width: 500px; margin: 0 auto; text-align: center; }
.not-found h1 { font-size: 36px; letter-spacing: -1.6px; margin: 0 0 14px; }

@media (max-width: 650px) {
  .guide-article h1 { font-size: 32px; }
  .cta-strip { flex-direction: column; align-items: flex-start; }
}
</style>
